import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';
import ImagePlaceholder from '../components/ImagePlaceholder';
import StaticMenuLayout from '../components/StaticMenuLayout';
import { RootState, AppDispatch } from '../store/store';
import { fetchMenuItems, fetchMenuCategories } from '../store/slices/menuSlice';
import { addItem } from '../store/slices/cartSlice';
import { MenuItem } from '../types/menu';


// Memoized Items Header Component - only updates when item count changes
const ItemsHeader: React.FC<{
  itemCount: number;
}> = React.memo(({ itemCount }) => (
  <div className="flex items-center justify-between mb-6">
    <p className="text-gray-600">
      Showing {itemCount} item{itemCount !== 1 ? 's' : ''}
    </p>
    <button className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium">
      <MicrophoneIcon className="w-4 h-4" />
      <span>Voice Order</span>
    </button>
  </div>
));

// Separate ItemCard component to prevent re-creation on every render
const ItemCard: React.FC<{
  item: MenuItem;
  onDetailsClick: (item: MenuItem) => void;
  onAddToCart: (item: MenuItem) => void;
}> = React.memo(({ item, onDetailsClick, onAddToCart }) => (
  <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
    <ImagePlaceholder
      src={item.image}
      alt={item.itemName}
      className="w-full h-48"
      fallbackSize="md"
    />
    <div className="p-6">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-semibold text-gray-900 flex-1">
          {item.itemName}
        </h3>
        {item.popularityScore > 8 && (
          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full ml-2">
            Featured
          </span>
        )}
      </div>

      {item.description && (
        <p className="text-gray-600 mb-4 text-sm line-clamp-2">
          {item.description}
        </p>
      )}

      {/* Voice Alias */}
      {item.voiceAliases && item.voiceAliases.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center">
            <MicrophoneIcon className="w-3 h-3 mr-1" />
            Say: "{item.voiceAliases[0]}"
          </p>
        </div>
      )}

      {/* Dietary Info */}
      {item.dietaryInfo && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {[item.dietaryInfo].map((diet: string, index: number) => (
              <span
                key={index}
                className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full"
              >
                {diet.trim()}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Prep Time */}
      {item.prepTime && (
        <p className="text-xs text-gray-500 mb-4">
          ⏱️ {item.prepTime} min prep time
        </p>
      )}

      <div className="flex items-center justify-between">
        <span className="text-2xl font-bold text-primary-600">
          ${item.standardRate}
        </span>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => onDetailsClick(item)}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Details
          </button>
          <button
            onClick={() => onAddToCart(item)}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-1 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add</span>
          </button>
        </div>
      </div>
    </div>
  </div>
));

// Create a separate container component to isolate state updates
const MenuContent: React.FC<{
  items: MenuItem[];
  onDetailsClick: (item: MenuItem) => void;
  onAddToCart: (item: MenuItem) => void;
}> = React.memo(({ items, onDetailsClick, onAddToCart }) => {
  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 text-lg mb-4">
          No items found matching your criteria
        </p>
      </div>
    );
  }

  return (
    <>
      <ItemsHeader itemCount={items.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((item) => (
          <ItemCard
            key={item.itemCode}
            item={item}
            onDetailsClick={onDetailsClick}
            onAddToCart={onAddToCart}
          />
        ))}
      </div>
    </>
  );
});

const MenuPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'all'
  );
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  const { items, categories, isLoading, error } = useSelector(
    (state: RootState) => state.menu
  );

  // Fetch categories on mount only
  useEffect(() => {
    dispatch(fetchMenuCategories());
  }, [dispatch]);

  // Fetch menu items when component mounts or category changes
  useEffect(() => {
    const params: any = {};
    if (selectedCategory !== 'all') {
      params.category = selectedCategory;
    }
    dispatch(fetchMenuItems(params));
  }, [selectedCategory, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    setSearchParams(params);
  }, [searchTerm, selectedCategory, setSearchParams]);

  // Client-side filtering to avoid Redux updates during typing
  const filteredItems = useMemo(() => {
    let filtered = items;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.itemName.toLowerCase().includes(searchLower) ||
        (item.description && item.description.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [items, searchTerm]);

  // Memoize categories to prevent SearchAndFilters re-rendering
  const memoizedCategories = useMemo(() => categories, [categories]);

  const handleAddToCart = useCallback((item: MenuItem, quantity: number = 1) => {
    dispatch(addItem({
      itemCode: item.itemCode,
      itemName: item.itemName,
      quantity,
      rate: item.standardRate,
    }));
  }, [dispatch]);

  const handleDetailsClick = useCallback((item: MenuItem) => {
    setSelectedItem(item);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory('all');
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleModalBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedItem(null);
    }
  }, []);

  // Handle ESC key for modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedItem) {
        setSelectedItem(null);
      }
    };

    if (selectedItem) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [selectedItem]);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading menu: {error}</p>
          <button
            onClick={() => dispatch(fetchMenuItems({}))}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <StaticMenuLayout
        searchTerm={searchTerm}
        selectedCategory={selectedCategory}
        categories={memoizedCategories}
        onSearchChange={handleSearchChange}
        onCategoryChange={handleCategoryChange}
      >
        {filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg mb-4">
              No items found matching your criteria
            </p>
            <button
              onClick={handleClearFilters}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <MenuContent
            items={filteredItems}
            onDetailsClick={handleDetailsClick}
            onAddToCart={handleAddToCart}
          />
        )}
      </StaticMenuLayout>

      {/* Item Detail Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleModalBackdropClick}
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedItem.itemName}
                </h2>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <ImagePlaceholder
                src={selectedItem.image}
                alt={selectedItem.itemName}
                className="w-full h-64 rounded-lg mb-4"
                fallbackSize="lg"
              />

              <div className="space-y-4">
                {selectedItem.description && (
                  <p className="text-gray-700">{selectedItem.description}</p>
                )}

                {selectedItem.voiceAliases && selectedItem.voiceAliases.length > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-1">Voice Command</h4>
                    <p className="text-blue-800">Say: "{selectedItem.voiceAliases[0]}"</p>
                  </div>
                )}

                {selectedItem.dietaryInfo && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Dietary Information</h4>
                    <div className="flex flex-wrap gap-2">
                      {[selectedItem.dietaryInfo].map((diet: string, index: number) => (
                        <span
                          key={index}
                          className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                        >
                          {diet.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedItem.prepTime && (
                  <div className="flex items-center text-gray-600">
                    <span className="mr-2">⏱️</span>
                    <span>Preparation time: {selectedItem.prepTime} minutes</span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className="text-3xl font-bold text-primary-600">
                    ${selectedItem.standardRate}
                  </span>
                  <button
                    onClick={() => {
                      handleAddToCart(selectedItem);
                      handleModalClose();
                    }}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Add to Cart</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuPage;